/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';

import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { UomDetailsDto } from './uom-details.dto';
import { UomDetailsMapper } from './uom-details.mapper';
import { UomListItemDto } from './uom-list-item.dto';
import { UomListItemMapper } from './uom-list-item.mapper';
import { UomSharedListItemDto, UomSharedListItemMapper } from './uom-shared-list-item.dto';

@Injectable()
export class UomRestService extends AbstractRestService {

  constructor(settings: RestSettings) {
    super(settings);
  }

  findAllAsDataSource(): SlicedDataSource<UomListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/units-of-measures`, arrayOf(UomListItemMapper));
  }

  findAllShared(): Observable<UomSharedListItemDto[]> {
    return this.httpGet(`${this.bp}/units-of-measures/shared`, arrayOf(UomSharedListItemMapper));
  }

  getShared(id: ID): Observable<UomDetailsDto> {
    return this.httpGet(`${this.bp}/units-of-measures/${id}/shared`, optimisticLockingOf(UomDetailsMapper));
  }

  remove(uomItem: UomListItemDto): Observable<UomListItemDto> {
    return this.httpDelete(`${this.bp}/units-of-measures/${uomItem.id}`, uomItem, optimisticLockingOf(UomListItemMapper));
  }

  checkUsage(uomItem: UomListItemDto): Observable<UomListItemDto> {
    return this.httpGet(`${this.bp}/units-of-measures/check-usage/${uomItem.id}`, optimisticLockingOf(UomListItemMapper));
  }

  get(id: ID): Observable<UomDetailsDto> {
    return this.httpGet(`${this.bp}/units-of-measures/${id}`, optimisticLockingOf(UomDetailsMapper));
  }

  update(uomDetails: UomDetailsDto): Observable<UomDetailsDto> {
    return this.httpPut(`${this.bp}/units-of-measures/${uomDetails.id}`, uomDetails, optimisticLockingOf(UomDetailsMapper));
  }

  create(uomDetails: UomDetailsDto): Observable<UomDetailsDto> {
    return this.httpPost(`${this.bp}/units-of-measures`, uomDetails, optimisticLockingOf(UomDetailsMapper));
  }

  bulkDelete(items: any[]): Observable<any> {
    return this.httpDelete(`${this.bp}/units-of-measures`, items, identity());
  }
}
